<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                建立批次操作
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋操作名稱或原因..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterOperationType" class="form-control" @change="handleFilterChange">
          <option value="">全部類型</option>
          <option value="update_members">更新會員</option>
          <option value="adjust_points">調整點數</option>
          <option value="issue_coupons">發放優惠券</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="pending">待執行</option>
          <option value="processing">執行中</option>
          <option value="completed">已完成</option>
          <option value="failed">失敗</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchBatchOperations">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>批次 ID</th>
            <th>操作類型</th>
            <th>操作名稱</th>
            <th>狀態</th>
            <th>影響會員數</th>
            <th>成功/失敗</th>
            <th>進度</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="batchOperations.length === 0">
            <td colspan="9" class="empty-state">
              沒有找到批次操作
            </td>
          </tr>
          <tr v-for="op in batchOperations" :key="op.id">
            <td>
              <code class="code-badge">{{ op.batchId }}</code>
            </td>
            <td>{{ getOperationTypeText(op.operationType) }}</td>
            <td>{{ op.operationName }}</td>
            <td>
              <span :class="getStatusBadgeClass(op.status)">
                {{ getStatusText(op.status) }}
              </span>
            </td>
            <td>{{ op.previewCount || op.totalCount || 0 }}</td>
            <td>
              <span v-if="op.status === 'completed'">
                {{ op.successCount || 0 }} / {{ op.failedCount || 0 }}
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <div v-if="op.status === 'processing'" class="progress-bar">
                <div class="progress-fill" :style="{ width: `${op.progressPercentage || 0}%` }"></div>
                <span class="progress-text">{{ op.progressPercentage || 0 }}%</span>
              </div>
              <span v-else>-</span>
            </td>
            <td>{{ formatDateTime(op.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleView(op.batchId)">
                  <i class="ri-eye-line"></i>
                  查看
                </button>
                <button 
                  v-if="op.status === 'draft' || op.status === 'pending'"
                  class="btn btn-sm btn-primary" 
                  @click="handleExecute(op.batchId)"
                >
                  <i class="ri-play-line"></i>
                  執行
                </button>
                <button 
                  v-if="op.status === 'draft' || op.status === 'pending'"
                  class="btn btn-sm btn-danger" 
                  @click="handleDelete(op.batchId, op.operationName)"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination && pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="changePage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { batchOperationService } from '@/api/modules/batchOperations.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientBatchOperations',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次操作管理');
    const batchOperations = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const pagination = ref(null);
    const searchQuery = ref('');
    const filterOperationType = ref('');
    const filterStatus = ref('');

    const fetchBatchOperations = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }
        if (filterOperationType.value) {
          params.operationType = filterOperationType.value;
        }
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        const response = await batchOperationService.getBatchOperations(params);

        if (response && response.success && response.data) {
          batchOperations.value = response.data.items || [];
          pagination.value = response.data.pagination;
        } else {
          error.value = response?.message || '獲取批次操作列表失敗';
        }
      } catch (err) {
        console.error('獲取批次操作列表失敗:', err);
        error.value = err.message || '獲取批次操作列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (pagination.value) {
        pagination.value.page = 1;
      }
      fetchBatchOperations();
    };

    const handleFilterChange = () => {
      if (pagination.value) {
        pagination.value.page = 1;
      }
      fetchBatchOperations();
    };

    const changePage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
      }
      fetchBatchOperations();
    };

    const handleCreate = () => {
      router.push('/client/batch-operations/create');
    };

    const handleView = (batchId) => {
      router.push(`/client/batch-operations/${batchId}`);
    };

    const handleExecute = async (batchId) => {
      const confirmed = await showConfirm(
        '確認執行',
        '確定要執行此批次操作嗎？執行後無法取消。'
      );

      if (!confirmed) return;

      try {
        const response = await batchOperationService.executeBatchOperation(batchId);

        if (response && response.success) {
          await showSuccess('執行成功', '批次操作已開始執行');
          await fetchBatchOperations();
        } else {
          await showError('執行失敗', response?.message || '執行批次操作失敗');
        }
      } catch (err) {
        console.error('執行批次操作失敗:', err);
        await showError('執行失敗', err.message || '執行批次操作失敗');
      }
    };

    const handleDelete = async (batchId, operationName) => {
      const confirmed = await showConfirm(
        '確認刪除',
        `確定要刪除批次操作「${operationName}」嗎？此操作無法復原。`
      );

      if (!confirmed) return;

      try {
        const response = await batchOperationService.deleteBatchOperation(batchId);

        if (response && response.success) {
          await showSuccess('刪除成功', '批次操作已刪除');
          await fetchBatchOperations();
        } else {
          await showError('刪除失敗', response?.message || '刪除批次操作失敗');
        }
      } catch (err) {
        console.error('刪除批次操作失敗:', err);
        await showError('刪除失敗', err.message || '刪除批次操作失敗');
      }
    };

    const getOperationTypeText = (type) => {
      const map = {
        'update_members': '更新會員',
        'adjust_points': '調整點數',
        'issue_coupons': '發放優惠券',
        'void_coupons': '作廢優惠券',
        'send_messages': '發送訊息',
        'export_members': '匯出會員'
      };
      return map[type] || type;
    };

    const getStatusText = (status) => {
      const map = {
        'draft': '草稿',
        'pending': '待執行',
        'processing': '執行中',
        'completed': '已完成',
        'failed': '失敗',
        'cancelled': '已取消'
      };
      return map[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const map = {
        'draft': 'badge badge-secondary',
        'pending': 'badge badge-warning',
        'processing': 'badge badge-info',
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger',
        'cancelled': 'badge badge-secondary'
      };
      return map[status] || 'badge badge-secondary';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    onMounted(() => {
      fetchBatchOperations();
    });

    return {
      pageTitle,
      batchOperations,
      loading,
      error,
      pagination,
      searchQuery,
      filterOperationType,
      filterStatus,
      fetchBatchOperations,
      handleSearch,
      handleFilterChange,
      changePage,
      handleCreate,
      handleView,
      handleExecute,
      handleDelete,
      getOperationTypeText,
      getStatusText,
      getStatusBadgeClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.progress-bar {
  position: relative;
  width: 100px;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: bold;
  color: #333;
  z-index: 1;
}
</style>
